import ColorFilter from '../../../Component/ColorFilter';
import Interactable from '../../../Component/Interactable';
import Renderable from '../../../Component/Renderable';
import ElementComponent from '../../../Component/UI/Element';
import UITextComponent from '../../../Component/UI/Text';
import Game from '../../../Game';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import { BoosterType } from '../../../State/Card/BoosterType';
import { CardActions } from '../../../State/Card/CardActions';
import CardStateHelper from '../../../State/Card/CardStateHelper';
import { IGameState } from '../../../State/Game/IGameState';
import { LevelComponent, LevelStatus } from '../../../State/Level/ILevelState';
import { UIActions } from '../../../State/UI/UIActions';
import { noCoinsPopupID } from '../../../State/UI/UIStateHelper';
import { StartAnimation } from '../../Animation/AnimationHelper';
import {
    AUDIO_UI_BUTTON_DEFAULT,
    AUDIO_UI_SUITCASE_CLOSE,
    AUDIO_UI_SUITCASE_OPEN,
} from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import { StartAudio } from '../../Audio/AudioHelper';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import BaseUISystem from '../BaseUISystem';
import {
    applyBounceButtonAnimation,
    applyDefaultButtonAnimation,
} from '../UIHelper';
import BaseMenu, { MenuState } from './BaseMenu';

const BOTTOM: string = 'bottom_container';
const BOOSTERS: string = `${BOTTOM}/suitcase_boosters`;
const SUITCASE_OPEN: string = `${BOTTOM}/suitcase_open_button`;
const SUITCASE_CLOSE: string = `${BOTTOM}/suitcase_close_button`;
const TAP_ME: string = `${SUITCASE_CLOSE}/tap_me`;
const BTN_INFO: string = `${BOOSTERS}/button_info`;
const TICKETS: string = `${BOOSTERS}/tickets_mask/tickets_container`;
const TICKET_TNT = `${TICKETS}/ticket1`;
const TICKET_BULLSEYE = `${TICKETS}/ticket2`;
const TICKET_SHUFFLE = `${TICKETS}/ticket3`;
const BTN_TNT: string = `${TICKETS}/ticket1/button`;
const BTN_BULLSEYE: string = `${TICKETS}/ticket2/button`;
const BTN_SHUFFLE: string = `${TICKETS}/ticket3/button`;
const BTN_CONTINUE: string = 'ftux/continue_button';

export default class SuitcaseBoosterHandler extends BaseMenu {
    private infoHidden: boolean;
    private suitcaseStartHidden: boolean;
    private tryMe: GameObject;

    private enabled: boolean = true;
    private isOpen: boolean = false;

    private btnInfo: GameObject;

    constructor(uiSystem: BaseUISystem) {
        super('suitcaseBoosters', uiSystem);

        this.alwaysVisible = true;
    }

    public handleStateChanged(state: IGameState) {
        // prettier-ignore
        if (state.levelState.components.indexOf(LevelComponent.SUITCASE) !== -1) {
            this.element.getComponent(Renderable).visible = true;
            if (state.levelState.levelNumber === 26 && this.suitcaseStartHidden) {
                this.element.find(BOTTOM).getComponent(Renderable).visible = false;
            }
        } else {
            this.element.getComponent(Renderable).visible = false;
        }

        this.updateTickets(state);

        this.updateNotification(state);

        // Only enable when the level is active.
        this.enableInteractables(
            state.levelState.status === LevelStatus.ACTIVE && this.enabled
        );
    }

    protected onCreate() {
        // Show and hide animations are different on Suitcase.
        this.clipShow = 'SuitcaseOpen';
        this.clipHide = 'SuitcaseClose';

        // Booster info start hidden. This avoid to run animation more than once.
        this.infoHidden = true;

        // Used when open level 26 and unlock the suitcase for the first time.
        this.suitcaseStartHidden =
            Game.state.getState().playerState.playerUnlocks.length === 0;

        // Wait level task emit this event.
        this.system.scene.on('SuitcaseFTUX', this.showFTUX.bind(this));

        this.system.scene.on('ToggleLevelHUD', (toggle: boolean) => {
            this.enabled = toggle;
            this.enableInteractables(
                this.system.state.getState().levelState.status ===
                    LevelStatus.ACTIVE && this.enabled
            );
        });

        this.btnInfo = this.element.find(BTN_INFO);

        const btnSuitcaseClose = this.element.find(SUITCASE_CLOSE);
        btnSuitcaseClose.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.showPopup(this.name));
        });
        applyDefaultButtonAnimation(btnSuitcaseClose, AUDIO_UI_BUTTON_DEFAULT);

        const btnSuitcaseOpen = this.element.find(SUITCASE_OPEN);
        btnSuitcaseOpen.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.hidePopup(this.name));
        });

        const btnInfo = this.element.find(BTN_INFO);
        btnInfo.on(GameObjectEvent.SELECTED, () => {
            if (this.infoHidden) {
                this.infoHidden = false;
                if (this.tryMe) {
                    this.tryMe.getComponent(Renderable).visible = false;
                }
                StartAnimation(this.element, 'SuitcaseShowInfo');
            }
        });

        const btnContinue = this.element.find(BTN_CONTINUE);
        btnContinue.on(GameObjectEvent.POINTER_UP, () => {
            this.system.overlayPopup.getComponent(Renderable).alpha = 0;
            StartAnimation(this.element, 'SuitcaseTutorialClose');
        });
        applyBounceButtonAnimation(btnContinue, AUDIO_UI_BUTTON_DEFAULT);

        // Setup booster buttons.
        this.setupBoosterButton(BTN_TNT, BoosterType.TNT);
        this.setupBoosterButton(BTN_BULLSEYE, BoosterType.BULLS_EYE);
        this.setupBoosterButton(BTN_SHUFFLE, BoosterType.SHUFFLE);
    }

    protected onDestroy(): void {
        this.system.scene.off('SuitcaseFTUX');
        this.system.scene.off('ToggleLevelHUD');
    }

    protected onShow(): void {
        StartAudio(
            this.system.scene,
            AUDIO_UI_SUITCASE_OPEN,
            AudioTrackType.SFX
        );

        this.isOpen = true;
        this.updateTickets(this.system.state.getState());
    }

    protected onShown(): void {
        this.system.overlayPopup.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.hidePopup(this.name));
        });

        this.element.find(TAP_ME).getComponent(Renderable).visible = false;

        if (this.tryMe) {
            this.tryMe.getComponent(Renderable).visible = true;
        }
    }

    protected onHide(): void {
        this.system.overlayPopup.off(GameObjectEvent.SELECTED);

        if (this.tryMe) {
            this.tryMe.getComponent(Renderable).visible = false;
        }

        this.infoHidden = true;

        StartAudio(
            this.system.scene,
            AUDIO_UI_SUITCASE_CLOSE,
            AudioTrackType.SFX
        );
    }

    protected onHidden(): void {
        const state = this.system.state.getState();

        this.enableInteractables(
            state.levelState.status === LevelStatus.ACTIVE && this.enabled
        );

        this.isOpen = false;
        this.updateTickets(state);
    }

    protected enableInteractables(value: boolean): void {
        super.enableInteractables(value);
        // prettier-ignore
        this.btnInfo.getComponent(Interactable).enabled = value && this.currentState === MenuState.OPEN;
    }

    private setupBoosterButton(
        buttonPath: string,
        boosterType: BoosterType
    ): void {
        const button = this.element.find(buttonPath);
        button.on(GameObjectEvent.SELECTED, () => {
            const state = this.system.state.getState();
            const boosterBehavior = CardStateHelper.getBoosterBehavior(
                state,
                boosterType
            );

            // If there is no playable cards available, shake the button.
            if (!boosterBehavior.hasEnoughTargetCards(state)) {
                StartAnimation(button, 'SuitcaseBoosterShake');
            } else {
                if (boosterBehavior.isPlayable(state)) {
                    this.system.state.dispatch(
                        CardActions.playBooster(boosterType)
                    );
                } else {
                    this.system.state.dispatch(
                        UIActions.showPopup(noCoinsPopupID(state.levelState))
                    );
                }
            }
        });
    }

    private getTicketProperties(
        state: IGameState,
        boosterType: BoosterType
    ): { ticket: string; items: number; price: number } {
        switch (boosterType) {
            case BoosterType.TNT:
                return {
                    ticket: TICKET_TNT,
                    items: state.playerState.inventory.boosterTNT,
                    price: state.levelState.boosterPrices.tnt,
                };
            case BoosterType.BULLS_EYE:
                return {
                    ticket: TICKET_BULLSEYE,
                    items: state.playerState.inventory.boosterBullsEye,
                    price: state.levelState.boosterPrices.bullsEye,
                };
            case BoosterType.SHUFFLE:
                return {
                    ticket: TICKET_SHUFFLE,
                    items: state.playerState.inventory.boosterShuffle,
                    price: state.levelState.boosterPrices.shuffle,
                };
        }
    }

    private updateTickets(state: IGameState): void {
        // prettier-ignore
        if (this.isOpen && state.levelState.components.indexOf(LevelComponent.BOOSTER_TNT) !== -1) {
            this.element.find(TICKET_TNT).getComponent(Renderable).visible = true;
            this.updateTicket(state, BoosterType.TNT);
        } else {
            const ticket = this.element.find(TICKET_TNT);
            ticket.getComponent(Renderable).visible = false;
            ticket.find('button').removeComponent(ColorFilter);
        }

        // prettier-ignore
        if (this.isOpen && state.levelState.components.indexOf(LevelComponent.BOOSTER_BULLS_EYE) !== -1) {
            this.element.find(TICKET_BULLSEYE).getComponent(Renderable).visible = true;
            this.updateTicket(state, BoosterType.BULLS_EYE);
        } else {
            const ticket = this.element.find(TICKET_BULLSEYE);
            ticket.getComponent(Renderable).visible = false;
            ticket.find('button').removeComponent(ColorFilter);
        }

        // prettier-ignore
        if (this.isOpen && state.levelState.components.indexOf(LevelComponent.BOOSTER_SHUFFLE) !== -1) {
            this.element.find(TICKET_SHUFFLE).getComponent(Renderable).visible = true;
            this.updateTicket(state, BoosterType.SHUFFLE);
        } else {
            const ticket = this.element.find(TICKET_SHUFFLE);
            ticket.getComponent(Renderable).visible = false;
            ticket.find('button').removeComponent(ColorFilter);
        }
    }

    private updateTicket(state: IGameState, boosterType: BoosterType): void {
        const { ticket, items, price } = this.getTicketProperties(
            state,
            boosterType
        );

        // Show number of items in inventory or the price.
        const txtTicket = this.element.find(`${ticket}/text`);
        const coinIconTicket = this.element.find(`${ticket}/coin_icon`);
        if (items > 0) {
            let free = Game.locale.getText('FREE');
            free = free.charAt(0).toUpperCase() + free.slice(1).toLowerCase();
            txtTicket.getComponent(UITextComponent).text = `${free} x${items}`;
            txtTicket.getComponent(ElementComponent).pivot.x = 0.5;
            coinIconTicket.getComponent(Renderable).visible = false;
        } else {
            txtTicket.getComponent(UITextComponent).text = price.toString();
            txtTicket.getComponent(ElementComponent).pivot.x = 0.2;
            coinIconTicket.getComponent(Renderable).visible = true;
        }

        // Update button color filter.
        const btn = this.element.find(`${ticket}/button`);

        const boosterBehavior = CardStateHelper.getBoosterBehavior(
            state,
            boosterType
        );

        const isPlayable = boosterBehavior.isPlayable(state);
        if (isPlayable) {
            btn.removeComponent(ColorFilter);
        } else {
            btn.addComponent(ColorFilter);
            btn.getComponent(ColorFilter).saturation = 0;
        }
    }

    private updateNotification(state: IGameState): void {
        const notification = this.element.find(
            `${SUITCASE_CLOSE}/notification`
        );
        const value =
            state.playerState.inventory.boosterBullsEye +
            state.playerState.inventory.boosterShuffle +
            state.playerState.inventory.boosterTNT;
        if (value > 0) {
            notification.getComponent(Renderable).visible = true;
            notification
                .find('text')
                .getComponent(UITextComponent).text = value.toString();
        } else {
            notification.getComponent(Renderable).visible = false;
        }
    }

    private showFTUX(booster: number) {
        this.system.scene.off('SuitcaseFTUX');

        const ftuxItem = this.element.find('ftux/unlocked_item');
        const ftuxText = this.element.find('ftux/unlocked_text');
        let name;
        let desc;

        switch (booster) {
            case 1:
                ftuxItem.children[0].getComponent(Renderable).visible = true;
                ftuxItem.children[1].getComponent(Renderable).visible = false;
                ftuxItem.children[2].getComponent(Renderable).visible = false;
                desc = Game.locale.getText('SuitcaseTutorial');
                ftuxText.getComponent(UITextComponent).text = desc;
                this.tryMe = this.element.find(`${BOOSTERS}/try_me/try_me1`);
                break;
            case 2:
                ftuxItem.children[0].getComponent(Renderable).visible = false;
                ftuxItem.children[1].getComponent(Renderable).visible = true;
                ftuxItem.children[2].getComponent(Renderable).visible = false;
                name = Game.locale.getText('BullseyeBooster');
                desc = Game.locale.getText('BullseyeBoosterText');
                ftuxText.getComponent(
                    UITextComponent
                ).text = `${name}\n${desc}`;
                this.tryMe = this.element.find(`${BOOSTERS}/try_me/try_me2`);
                break;
            case 3:
                ftuxItem.children[0].getComponent(Renderable).visible = false;
                ftuxItem.children[1].getComponent(Renderable).visible = false;
                ftuxItem.children[2].getComponent(Renderable).visible = true;
                name = Game.locale.getText('ShuffleBooster');
                desc = Game.locale.getText('ShuffleBoosterText');
                ftuxText.getComponent(
                    UITextComponent
                ).text = `${name}\n${desc}`;
                this.tryMe = this.element.find(`${BOOSTERS}/try_me/try_me3`);
                break;
        }

        // prettier-ignore
        this.system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, this.system.overlayPopup);
        // prettier-ignore
        this.system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, this.element);
        // prettier-ignore
        this.system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, this.system.UIOverlay);
        StartAnimation(this.system.overlayPopup, 'OverlayFadeIn');
        StartAnimation(this.element, 'SuitcaseTutorialOpen');

        this.suitcaseStartHidden = false;
    }
}
